import React, { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, styled, InputAdornment, IconButton } from "@mui/material";
import ContentUsBgImage from "@assets/ContentUsBgImage.png";
import {
  completeOidcLogin,
  finalizeOidcCallbackClaim,
  loginWithEmailPassword,
  redirectToOidcLogin,
  releaseOidcCallbackClaim,
  stripOidcReturnParams,
  tryClaimOidcCallback,
  waitForOidcCallbackDone,
} from "@services/auth";
import { useNavigate, useLocation } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { preloadCountriesOnLogin } from "@utils/countryUtils";
import { tryGetSinglePaymentPendingFacilityForPricing } from "@utils/verifiedFacilityRedirect";
import { resolveFacilityAccessBlocked } from "@services/facilityManagement";
import { getSsoButtonSettings, SsoButtonsSettings } from "../../services/ssoSettings";
import {
  authPanelTaglineForVertical,
  landingHomePathForVertical,
  landingVerticalFromSearch,
} from "@utils/appPath";
import { setLandingVerticalSession, syncPreviewVerticalSession } from "@services/verticals";
import { parseAuthErrorMessage } from "@utils/ssoErrorMessages";

const SignUpContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#ffffff",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  padding: "16px",
  [theme.breakpoints.down("sm")]: {
    padding: "8px",
    alignItems: "flex-start",
    paddingTop: "20px",
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 0px",
  position: "relative",
  width: "100%",
  maxWidth: "900px",
  margin: "0 auto",
  gap: 16,
  [theme.breakpoints.down("lg")]: {
    gap: 12,
    flexDirection: "column",
    padding: "12px",
    maxWidth: "900px",
  },
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    padding: "16px 8px",
    gap: 10,
    maxWidth: "100%",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "12px 6px",
    gap: 8,
  },
}));

const BackgroundOverlay = styled(Box)({
  position: "absolute",
  height: "100%",
  left: 0,
  top: 0,
  width: "100%",
  backgroundImage: `
    radial-gradient(ellipse 42.02px 68.593px at 298.8px 124.35px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 77.28px 6.0674px at 1471.2px 363.44px, rgba(220,253,255,0.4) 0%, rgba(255,255,255,0) 100%),
    radial-gradient(ellipse 1.62px 57.671px at 682.2px 879.48px, rgba(197,255,225,0.4) 0%, rgba(255,255,255,0) 100%)
  `,
});

const GradientBackground = styled(Box)({
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(ellipse at 138px 40px, #065b70 0%, #0a829d 100%)",
  opacity: 0.9,
  backgroundImage: `url(${ContentUsBgImage})`,
  width: "100%",
  height: "100%",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
});

const LeftPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "620px",
  height: "600px",
  backgroundImage: `url(${ContentUsBgImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  borderRadius: "32px",
  border: "1px solid #ffffff",
  overflow: "hidden",
  marginRight: "-80px",
  flexShrink: 0,
  [theme.breakpoints.down("lg")]: {
    width: "480px",
    height: "440px",
    marginRight: "-20px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "500px",
    height: "auto",
    minHeight: "280px",
    aspectRatio: "16 / 10",
    marginRight: 0,
    borderRadius: "24px",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    minHeight: "240px",
    borderRadius: "16px",
  },
}));

const LeftPanelContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: "95.5px",
  top: "101px",
  width: "544px",
  color: "#ffffff",
  [theme.breakpoints.down("lg")]: {
    left: "32px",
    top: "48px",
    width: "380px",
  },
  [theme.breakpoints.down("md")]: {
    position: "relative",
    left: 0,
    top: 0,
    width: "100%",
    padding: "16px 12px 12px",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "18px 0px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "64px",
  lineHeight: "1.1",
  marginBottom: "120px",
  marginRight: "120px",
  [theme.breakpoints.down("lg")]: {
    fontSize: "42px",
    marginBottom: "40px",
    marginRight: "32px",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
    marginBottom: "12px", // tighter gap to subtitle on tablets
    marginRight: 0,
    lineHeight: "1.2",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
    marginBottom: "12px",
    textAlign: "center",
    paddingTop: "48px",
  },
}));

const SubTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "1.5",
  width: "422px",
  whiteSpace: "normal",
  overflowWrap: "normal",
  wordBreak: "normal",
  [theme.breakpoints.down("lg")]: {
    width: "360px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    fontSize: "16px",
    lineHeight: "1.45",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: "1.4",
    maxWidth: 320,
    margin: "0 auto",
    textAlign: "center",
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#f4feff",
  border: "1px solid #05495a",
  borderRadius: "16px",
  padding: "28px 24px 28px 28px",
  width: "480px",
  position: "relative",
  flexShrink: 0,
  [theme.breakpoints.down("lg")]: {
    width: "420px",
    padding: "24px 20px 24px 24px",
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
    maxWidth: "480px",
    padding: "20px 16px 20px 20px",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "16px 12px 16px 16px",
    borderRadius: "12px",
    // Add a tiny horizontal margin so the right-side border is visible on small screens
    margin: "0 6px",
    maxWidth: "90%",
  },
}));

const CloseButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  right: "47px",
  top: "47px",
  minWidth: "28px",
  width: "28px",
  height: "28px",
  padding: 0,
  color: "#033843",
  "&:hover": {
    backgroundColor: "rgba(3, 56, 67, 0.04)",
  },
  [theme.breakpoints.down("sm")]: {
    right: "12px",
    top: "12px",
  },
}));

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  width: "100%",
});

const PageTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 700,
  fontSize: "28px",
  lineHeight: "35px",
  color: "#033843",
  height: "35px",
});

const SocialButtonsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "10px",
    width: "100%",
  },
}));

const SocialButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(180deg, #ffffff 0%, #f6feff 100%)",
  border: "1px solid #bfe5ec",
  borderRadius: "64px",
  height: "40px",
  padding: "4px 33px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  boxShadow:
    "0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  letterSpacing: "-0.4px",
  color: "#065b70",
  minHeight: "44px",
  "&:hover": {
    backgroundColor: "#f0f9fa",
    boxShadow:
      "0px 2px 4px -1px rgba(0,0,0,0.1), 0px 2px 6px 0px rgba(0,0,0,0.1)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    justifyContent: "center",
    fontSize: "16px",
    height: "48px",
    minHeight: "48px",
    padding: "12px 16px",
  },
}));

const OrDivider = styled(Box)({
  padding: "6px",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  width: "100%",
});

const DividerLine = styled(Box)({
  height: "1px",
  backgroundColor: "#e0e0e0",
  flex: 1,
});

const OrText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const InputContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  width: "100%",
});

const InputLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  color: "#262626",
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    height: "42px",
    borderRadius: "4px",
    "& fieldset": {
      borderColor: "#e6fafc",
    },
    "&:hover fieldset": {
      borderColor: "#bfe5ec",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0a829d",
    },
    boxShadow:
      "inset 0px 0px 8px 0px rgba(0,108,116,0.12), inset 0px 0px 4px 0px rgba(34,110,127,0.25)",
  },
  "& .MuiOutlinedInput-input": {
    padding: "11px 14px",
    fontSize: "14px",
    fontFamily: "Rethink Sans, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "16px",
      padding: "12px 14px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& .MuiOutlinedInput-root": {
      height: "48px",
      minHeight: "44px",
    },
  },
}));

const SignInButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
  border: "1px solid #ffffff",
  borderRadius: "60px",
  height: "60px",
  width: "100%",
  padding: "12px 24px",
  boxShadow:
    "0px 4px 24px 0px rgba(10,130,157,0.2), 0px 4px 7.3px 0px rgba(133,233,255,0.4)",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "22px",
  color: "#ffffff",
  minHeight: "44px",
  "&:hover": {
    background: "linear-gradient(90deg, #086e86 0%, #2bc9d6 100%)",
  },
  [theme.breakpoints.down("sm")]: {
    height: "52px",
    minHeight: "48px",
    fontSize: "16px",
    borderRadius: "48px",
  },
}));

const TermsContainer = styled(Box)({
  padding: "8px",
  display: "flex",
  flexDirection: "column",
  gap: "4.5px",
  alignItems: "center",
  width: "100%",
});

const TermsText = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "5px",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
});

const TermsLink = styled("span")({
  color: "#0a829d",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "#086e86",
  },
});

const SignUpContainerRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4.5px",
  width: "100%",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "20px",
  minHeight: "44px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    lineHeight: "24px",
    flexWrap: "wrap",
    textAlign: "center",
  },
}));

const SignUpLink = styled("span")(({ theme }) => ({
  color: "#0a829d",
  textDecoration: "underline",
  cursor: "pointer",
  padding: "8px",
  borderRadius: "4px",
  "&:hover": {
    color: "#086e86",
    backgroundColor: "rgba(10, 130, 157, 0.05)",
  },
  [theme.breakpoints.down("sm")]: {
    display: "inline-block",
    minHeight: "44px",
    lineHeight: "28px",
  },
}));

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [autofillBgColor, setAutofillBgColor] = useState("transparent");
  const passwordInputRef = React.useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);
  const ssoInFlightRef = React.useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [showSsoButtons, setShowSsoButtons] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const landingVertical = landingVerticalFromSearch(location.search);
  const panelTagline = authPanelTaglineForVertical(landingVertical);
  // First paint with ?code=&state= must not show the credential form (effect runs after paint).
  const isOidcCallback = (() => {
    const params = new URLSearchParams(location.search);
    return Boolean(params.get("code") && params.get("state"));
  })();
  const showSsoBusy = ssoBusy || isOidcCallback;
  const authBusy = loading || showSsoBusy;

  // Pin landing vertical as soon as /login?vertical=aviation mounts so
  // post-login facility loads never briefly scope to healthcare.
  useEffect(() => {
    if (landingVertical) {
      setLandingVerticalSession(landingVertical);
    }
  }, [landingVertical]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let isMounted = true;
    getSsoButtonSettings()
      .then((settings: SsoButtonsSettings) => {
        if (isMounted) {
          setShowSsoButtons(settings.login_sso !== false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setShowSsoButtons(true);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Complete OIDC authorization-code callback (?code=&state= from identity IdP).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      return;
    }

    const returnToCleanLogin = () => {
      const cleaned = stripOidcReturnParams(new URLSearchParams(location.search));
      navigate(
        { pathname: "/login", search: cleaned ? `?${cleaned}` : "" },
        { replace: true },
      );
    };

    const finishOidcReturn = async () => {
      // Success: keep the busy panel. Do not navigate back to a clean /login —
      // that paints the credential form for a frame before dashboard redirect.
      await preloadCountriesOnLogin();
      await redirectAfterLogin();
    };

    const ownsCallback = tryClaimOidcCallback(state);
    if (!ownsCallback) {
      let cancelled = false;
      (async () => {
        setSsoBusy(true);
        setError(null);
        try {
          const ready = await waitForOidcCallbackDone(state);
          if (cancelled) return;
          if (!ready && !localStorage.getItem("access_token")) {
            returnToCleanLogin();
            setError("SSO sign-in timed out. Please try again.");
            setSsoBusy(false);
            return;
          }
          await finishOidcReturn();
          // Keep busy until leave login; do not clear on success.
        } catch (e: unknown) {
          if (cancelled) return;
          returnToCleanLogin();
          setError(parseAuthErrorMessage(e, "SSO sign-in failed"));
          setSsoBusy(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    (async () => {
      setSsoBusy(true);
      setError(null);
      try {
        await completeOidcLogin({ state, code });
        finalizeOidcCallbackClaim(state);
        if (cancelled) return;
        await finishOidcReturn();
        // Keep busy until leave login; do not clear on success.
      } catch (e: unknown) {
        releaseOidcCallbackClaim(state);
        if (cancelled) return;
        returnToCleanLogin();
        setError(parseAuthErrorMessage(e, "SSO sign-in failed"));
        setSsoBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on OIDC return
  }, [location.search]);

  // Detect autofill background color on password field
  useEffect(() => {
    const checkAutofill = () => {
      if (passwordInputRef.current) {
        const input = passwordInputRef.current;
        const computedStyle = window.getComputedStyle(input);
        const bgColor = computedStyle.backgroundColor;
        setAutofillBgColor(bgColor);
      }
    };

    // Check on mount and periodically (autofill happens after render)
    checkAutofill();
    const timer = setTimeout(checkAutofill, 100);
    const interval = setInterval(checkAutofill, 500);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [password]);

  const redirectAfterLogin = async () => {
    // Landing routes (/aviation → /login?vertical=aviation) must open the
    // matching vertical in the dashboard, not the account default (healthcare).
    // Landing vertical scopes pricing + API (aviation open to any role).
    // Preview sync only sticks for executive / global-admin (V switcher).
    if (landingVertical) {
      setLandingVerticalSession(landingVertical);
      syncPreviewVerticalSession(landingVertical);
    }

    // Same path as signup: access gate, then /dashboard.
    // Empty users → Dashboard Case 1 setup popup. Users with facilities → Case 2/3.
    const blocked = await resolveFacilityAccessBlocked();
    if (blocked) {
      navigate("/access-removed", { replace: true });
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const returnTo = searchParams.get("return_to");
    if (
      returnTo &&
      returnTo.startsWith("/dashboard") &&
      !returnTo.includes("..")
    ) {
      navigate(returnTo, { replace: true });
      return;
    }

    // Check if URL has pricing redirect params
    const userPreferredFacilityId = searchParams.get("user_preferred_facility_id");
    const promoCode = searchParams.get("promo_code");

    if (userPreferredFacilityId) {
      const pricingParams = new URLSearchParams();
      pricingParams.set("user_preferred_facility_id", userPreferredFacilityId);
      if (promoCode) {
        pricingParams.set("promo_code", promoCode);
      }
      navigate(`/dashboard/facilities?${pricingParams.toString()}`, {
        replace: true,
        state: { currentView: "pricing" },
      });
      return;
    }

    // If user has only one preferred facility (for this landing vertical) and
    // payment is pending, redirect to pricing — never cross-vertical (e.g. Seagen
    // pharma when logging in via /aviation).
    const paymentPendingFacility = await tryGetSinglePaymentPendingFacilityForPricing(
      landingVertical,
    );
    if (paymentPendingFacility) {
      const facilityTypeId =
        (paymentPendingFacility.facility_details as any)?.facility_type_id ?? null;
      navigate("/dashboard/facilities", {
        replace: true,
        state: {
          currentView: "pricing",
          fromPreferredFacilityId: paymentPendingFacility.id,
          facility_type_id: facilityTypeId,
          pricingReloadToken: Date.now(),
        },
      });
      return;
    }

    navigate("/dashboard", { replace: true });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await loginWithEmailPassword(email, password);
      await preloadCountriesOnLogin();
      await redirectAfterLogin();
    } catch (err: unknown) {
      let errorMessage = "Login failed";
      let requiresOtpVerification = false;
      try {
        const message = err instanceof Error ? err.message : "";
        if (message.startsWith("{")) {
          const parsed = JSON.parse(message);
          errorMessage = parsed?.detail || parsed?.message || "Login failed";
          if (
            parsed?.is_verified === false ||
            errorMessage.toLowerCase().includes("email not verified") ||
            errorMessage.toLowerCase().includes("verify your email")
          ) {
            requiresOtpVerification = true;
            navigate("/email-verification", {
              state: { email, fromSignup: false, vertical: landingVertical },
            });
            return;
          }
        } else {
          errorMessage = message || "Login failed";
          if (
            errorMessage.toLowerCase().includes("email not verified") ||
            errorMessage.toLowerCase().includes("verify your email")
          ) {
            requiresOtpVerification = true;
            navigate("/email-verification", {
              state: { email, fromSignup: false, vertical: landingVertical },
            });
            return;
          }
        }
      } catch {
        errorMessage = err instanceof Error ? err.message : "Login failed";
      }
      if (!requiresOtpVerification) {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate(landingHomePathForVertical(landingVertical));
  };

  const handleSignUpClick = () => {
    // Aviation / food_service use lead form; healthcare account signup stays on /signup.
    if (landingVertical && landingVertical !== "healthcare") {
      navigate(`/signup-request?vertical=${landingVertical}`);
      return;
    }
    navigate(
      landingVertical ? `/signup?vertical=${landingVertical}` : "/signup",
    );
  };

  const handleTermsClick = () => {
    navigate("/terms-of-service");
  };

  const handlePrivacyClick = () => {
    navigate("/privacy-policy");
  };

  const handleSocialSignIn = async (provider: "Google" | "Microsoft") => {
    // Ignore re-entry while email or SSO is already in flight (double-click).
    if (loading || ssoInFlightRef.current) {
      return;
    }
    ssoInFlightRef.current = true;
    setError(null);
    setSsoBusy(true);
    try {
      await redirectToOidcLogin(provider === "Google" ? "google" : "microsoft");
      // Keep busy until browser navigates to the IdP; do not clear on success.
    } catch (err: unknown) {
      ssoInFlightRef.current = false;
      setError(parseAuthErrorMessage(err, `${provider} sign-in failed`));
      setSsoBusy(false);
    }
  };

  return (
    <SignUpContainer>
      <ContactSection>
        <BackgroundOverlay />

        <LeftPanel>
          <GradientBackground></GradientBackground>
          <LeftPanelContent>
            <MainTitle>Welcome Back to UniComply</MainTitle>
            <SubTitle>{panelTagline}</SubTitle>
          </LeftPanelContent>
        </LeftPanel>

        <RightPanel>
          {!showSsoBusy && (
          <CloseButton onClick={handleClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="29"
              height="29"
              viewBox="0 0 29 29"
              fill="none"
            >
              <g clipPath="url(#clip0_358_243)">
                <path
                  d="M14.5 0.25C6.76806 0.25 0.5 6.51806 0.5 14.25C0.5 21.9824 6.76806 28.25 14.5 28.25C22.2324 28.25 28.5 21.9824 28.5 14.25C28.5 6.51806 22.2324 0.25 14.5 0.25ZM14.5 26.5276C7.74544 26.5276 2.25 21.0046 2.25 14.2499C2.25 7.49538 7.74544 1.99995 14.5 1.99995C21.2546 1.99995 26.75 7.49541 26.75 14.2499C26.75 21.0045 21.2546 26.5276 14.5 26.5276ZM19.4494 9.30056C19.1077 8.95887 18.5539 8.95887 18.2122 9.30056L14.5 13.0128L10.7878 9.30056C10.4461 8.95887 9.89225 8.95887 9.55013 9.30056C9.20844 9.64225 9.20844 10.1961 9.55013 10.5378L13.2623 14.25L9.55013 17.9622C9.20844 18.3034 9.20844 18.8582 9.55013 19.1995C9.89181 19.5412 10.4457 19.5412 10.7878 19.1995L14.5 15.4873L18.2122 19.1995C18.5539 19.5412 19.1077 19.5412 19.4494 19.1995C19.7911 18.8582 19.7911 18.3034 19.4494 17.9622L15.7372 14.25L19.4494 10.5378C19.7916 10.1957 19.7916 9.64181 19.4494 9.30056Z"
                  fill="#033843"
                />
              </g>
              <defs>
                <clipPath id="clip0_358_243">
                  <rect
                    width="28"
                    height="28"
                    fill="white"
                    transform="translate(0.5 0.25)"
                  />
                </clipPath>
              </defs>
            </svg>
          </CloseButton>
          )}

          <FormContainer>
            <PageTitle>Sign In</PageTitle>

            {showSsoBusy ? (
              <Typography
                role="status"
                aria-live="polite"
                variant="body1"
                sx={{
                  color: "#065b70",
                  textAlign: "center",
                  fontFamily: "Rethink Sans, sans-serif",
                  fontWeight: 600,
                  py: 4,
                }}
                data-testid="login-sso-busy"
              >
                Signing you in…
              </Typography>
            ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              {showSsoButtons && (
                <>
                  <SocialButtonsContainer>
                    <SocialButton
                      type="button"
                      disabled={authBusy}
                      aria-busy={ssoBusy}
                      onClick={() => handleSocialSignIn("Google")}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: "16px",
                          height: "16px",
                          backgroundImage:
                            "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE1LjY4IDguMTgxODJIMTVWOEg4VjEwSDEyLjI5MDlDMTEuNzk4MiAxMS4zOTY0IDEwLjY1IDEyLjUgOS4yNSAxM0M3IDEzIDUuMTUgMTEuMTUgNS4xNSA4Ljg1QzUuMTUgNi41NSA3IDQuNjUgOS4yNSA0LjY1QzEwLjI5MTggNC42NSAxMS4yNjM2IDUuMDU0NTUgMTIgNS43NEwxMy40IDE0LjJDMTIuNiA0LjggMTAuNiAzIDggM0M0LjcgMyAyIDUuNyAyIDlTNC43IDE1IDggMTVTMTQgMTIuMyAxNCA5QzE0IDguNzI3MjcgMTMuODkwOSA4LjQ0NTQ1IDEzLjY4IDguMTgxODJaIiBmaWxsPSIjNEI1NTYzIi8+Cjwvc3ZnPgo=')",
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                        }}
                      />
                      Continue with Google
                    </SocialButton>

                    <SocialButton
                      type="button"
                      disabled={authBusy}
                      aria-busy={ssoBusy}
                      onClick={() => handleSocialSignIn("Microsoft")}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: "16px",
                          height: "16px",
                          backgroundImage:
                            "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMSIgeT0iMSIgd2lkdGg9IjYuNSIgaGVpZ2h0PSI2LjUiIGZpbGw9IiNGMjUwMjIiLz4KPHJlY3QgeD0iOC41IiB5PSIxIiB3aWR0aD0iNi41IiBoZWlnaHQ9IjYuNSIgZmlsbD0iIzc5QkE0MiIvPgo8cmVjdCB4PSIxIiB5PSI4LjUiIHdpZHRoPSI2LjUiIGhlaWdodD0iNi41IiBmaWxsPSIjMDA5OURGIi8+CjxyZWN0IHg9IjguNSIgeT0iOC41IiB3aWR0aD0iNi41IiBoZWlnaHQ9IjYuNSIgZmlsbD0iI0ZGQjkwMCIvPgo8L3N2Zz4K')",
                          backgroundSize: "contain",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center",
                        }}
                      />
                      Continue with Microsoft
                    </SocialButton>
                  </SocialButtonsContainer>

                  <OrDivider>
                    <DividerLine />
                    <OrText>OR</OrText>
                    <DividerLine />
                  </OrDivider>
                </>
              )}

              <Box
                component="form"
                onSubmit={onSubmit}
                sx={{ display: "flex", flexDirection: "column", gap: "24px" }}
              >
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
                >
                  <InputContainer>
                    <InputLabel>Email</InputLabel>
                    <StyledTextField
                      fullWidth
                      type="email"
                      variant="outlined"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@example.com"
                      required
                    />
                  </InputContainer>

                  <Box>
                    <InputContainer>
                      <InputLabel>Password</InputLabel>
                      <StyledTextField
                        fullWidth
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        required
                        autoComplete="current-password"
                        inputRef={passwordInputRef}
                        InputProps={{
                          style: { backgroundColor: autofillBgColor },
                          endAdornment: (
                            <InputAdornment 
                              position="end" 
                              sx={{ 
                                backgroundColor: autofillBgColor,
                                height: '100%',
                                margin: 0,
                                padding: 0,
                                paddingRight: 0,
                                marginRight: '-14px',
                              }}
                            >
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                disableRipple
                                sx={{
                                  color: "#666",
                                  padding: "12px 14px",
                                  margin: 0,
                                  backgroundColor: `${autofillBgColor} !important`,
                                  borderTopRightRadius: '8px',
                                  borderBottomRightRadius: '8px',
                                  borderTopLeftRadius: 0,
                                  borderBottomLeftRadius: 0,
                                  "&:hover": {
                                    backgroundColor: `${autofillBgColor} !important`,
                                  },
                                  "&:focus": {
                                    backgroundColor: `${autofillBgColor} !important`,
                                  },
                                  "&:active": {
                                    backgroundColor: `${autofillBgColor} !important`,
                                  },
                                }}
                              >
                                {showPassword ? (
                                  <VisibilityOff sx={{ fontSize: 20 }} />
                                ) : (
                                  <Visibility sx={{ fontSize: 20 }} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: autofillBgColor,
                            overflow: 'hidden',
                            "& fieldset": {
                              borderColor: "#bfe5ec",
                            },
                            // Hide browser's default password reveal button
                            "& input::-ms-reveal": {
                              display: "none",
                            },
                            "& input::-ms-clear": {
                              display: "none",
                            },
                          },
                          // Ensure only our icon shows
                          "& input[type='password']::-webkit-credentials-auto-fill-button": {
                            display: "none",
                          },
                        }}
                      />
                    </InputContainer>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "8px" }}>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "Rethink Sans, sans-serif",
                          fontWeight: 500,
                          fontSize: { xs: "16px", sm: "14px" },
                          color: "#0a829d",
                          cursor: "pointer",
                          textDecoration: "underline",
                          padding: "8px",
                          borderRadius: "4px",
                          minHeight: "44px",
                          display: "flex",
                          alignItems: "center",
                          "&:hover": {
                            color: "#086e86",
                            backgroundColor: "rgba(10, 130, 157, 0.05)",
                          },
                        }}
                        onClick={() =>
                          navigate(
                            landingVertical
                              ? `/forgot-password?vertical=${landingVertical}`
                              : "/forgot-password",
                          )
                        }
                      >
                        Forgot Password?
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {error && (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                )}

                <SignInButton type="submit" disabled={authBusy}>
                  {loading ? "Signing in..." : "SIGN IN"}
                </SignInButton>

                <TermsContainer>
                  <TermsText>
                    <span style={{ color: "#737373" }}>
                      By continuing, you agree to{" "}
                    </span>
                    <TermsLink onClick={handleTermsClick}>
                      UniComply.ai's Terms of Service
                    </TermsLink>
                  </TermsText>
                  <TermsText>
                    <span style={{ color: "#737373" }}>and </span>
                    <TermsLink onClick={handlePrivacyClick}>
                      Privacy Policy
                    </TermsLink>
                  </TermsText>
                </TermsContainer>
              </Box>
            </Box>
            )}

            {!showSsoBusy && (
            <SignUpContainerRow>
              <span style={{ color: "#737373" }}>Don’t have an account? </span>
              <SignUpLink onClick={handleSignUpClick}>Sign up</SignUpLink>
            </SignUpContainerRow>
            )}
          </FormContainer>
        </RightPanel>
      </ContactSection>
    </SignUpContainer>
  );
}
